import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import type { Submission } from '../../lib/types'
import { count } from '../../lib/stats'
import { C } from '../../lib/colors'

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend)

const BASE = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }
const GRID_COLOR = 'rgba(1,87,98,0.07)'

function fmtLoc(k: string): string {
  if (!k) return '—'
  if (k === 'outside-kenya') return 'Outside Kenya'
  if (k === 'other-kenya') return 'Other Kenya'
  const map: Record<string, string> = {
    'nairobi-central': 'Nairobi Central', 'nairobi-east': 'Nairobi East',
    'nairobi-west': 'Nairobi West', 'nairobi-north': 'Nairobi North', 'nairobi-south': 'Nairobi South',
  }
  return map[k] ?? k.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

interface Props { data: Submission[] }

export function Demographics({ data }: Props) {
  const N = data.length

  // Age
  const ageKeys   = ['under-18', '18-25', '26-35', '36-42', 'over-42']
  const ageLabels = ['<18', '18–25', '26–35', '36–42', '42+']
  const ageData   = ageKeys.map(k => count(data, d => d.age_bracket === k))

  // Location
  const locRaw: Record<string, number> = {}
  data.forEach(d => {
    if (!d.location) return
    const k = d.location.startsWith('outside-') ? 'outside-kenya' : d.location
    locRaw[k] = (locRaw[k] || 0) + 1
  })
  const locEntries = Object.entries(locRaw).sort((a, b) => b[1] - a[1]).slice(0, 10)
  const locLabels  = locEntries.map(([k]) => fmtLoc(k))
  const locValues  = locEntries.map(([, v]) => v)
  const locColors  = locEntries.map((_, i) =>
    [C.teal, C.a(C.teal,.8), C.a(C.teal,.65), C.a(C.teal,.5), C.a(C.teal,.38),
     C.teal2, C.a(C.teal2,.7), C.a(C.teal2,.5), C.a(C.rose,.5), C.a(C.rose,.35)][i] ?? C.a(C.teal,.3)
  )

  // Goal
  const goalKeys   = ['not-planning', 'future', 'ttc', 'ttc-6m-plus']
  const goalLabels = ['Not planning', 'Future', 'Actively TTC', 'TTC 6m+']
  const goalData   = goalKeys.map(k => count(data, d => d.fertility_goal === k))

  // Contraception
  const contraKeys   = ['none', 'current', 'stopped-lt-6m', 'stopped-6-12m', 'stopped-gt-1y']
  const contraLabels = ['Never used', 'Currently on', 'Stopped <6m', 'Stopped 6–12m', 'Stopped >1yr']
  const contraData   = contraKeys.map(k => count(data, d => d.contraception_status === k))

  void N

  return (
    <section className="sec" id="demographics">
      <div className="sec-hd">
        <div className="sec-label">01 · Demographics</div>
        <h2 className="sec-title">Who is taking this assessment?</h2>
        <p className="sec-sub">Age, location, fertility intention, and contraception status across the cohort.</p>
      </div>
      <div className="g4">
        <div className="card">
          <div className="card-title">Age distribution</div>
          <div className="card-sub">Respondents by age bracket</div>
          <div className="cw h200">
            <Bar
              data={{ labels: ageLabels, datasets: [{ data: ageData, backgroundColor: [C.a(C.teal,.4), C.a(C.teal,.6), C.teal, C.a(C.teal,.7), C.a(C.teal,.5)], borderRadius: 5 }] }}
              options={{ ...BASE, scales: { x: { grid: { display: false } }, y: { grid: { color: GRID_COLOR } } } }}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-title">Location</div>
          <div className="card-sub">Nairobi sub-zones and other regions</div>
          <div className="cw h200">
            <Bar
              data={{ labels: locLabels, datasets: [{ data: locValues, backgroundColor: locColors, borderRadius: 4 }] }}
              options={{ ...BASE, indexAxis: 'y' as const, scales: { x: { grid: { color: GRID_COLOR } }, y: { grid: { display: false } } } }}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-title">Fertility intention</div>
          <div className="card-sub">Conception plans at time of assessment</div>
          <div className="cw h200">
            <Bar
              data={{ labels: goalLabels, datasets: [{ data: goalData, backgroundColor: [C.a(C.teal,.4), C.teal, C.teal2, C.rose], borderRadius: 4 }] }}
              options={{ ...BASE, indexAxis: 'y' as const, scales: { x: { grid: { color: GRID_COLOR } }, y: { grid: { display: false } } } }}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-title">Contraception history</div>
          <div className="card-sub">Current or recent hormonal contraception use</div>
          <div className="cw h200">
            <Doughnut
              data={{ labels: contraLabels, datasets: [{ data: contraData, backgroundColor: [C.teal, C.rose, C.amber, C.mint, C.a(C.teal,.4)], borderWidth: 0 }] }}
              options={{ ...BASE, plugins: { legend: { display: true, position: 'bottom' as const, labels: { boxWidth: 10, padding: 10, font: { size: 10 } } } } }}
            />
          </div>
        </div>
      </div>
    </section>
  )
}
